import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { formatSize, parseSizeInput, snapSize, stepSize } from "@/lib/sizes";

// Size stepper: − / + walk real size steps (halves; thirds and halves for EU)
// and always show fractions (42½, 42⅔). A small numeric-keypad field is the
// fallback for typing; it accepts "," or "." and "42 2/3".
export default function SizeStepper({ unit = "EU", value = null, onChange, testid = "size-stepper", invalid = false }) {
  const [typed, setTyped] = useState("");
  const [typedError, setTypedError] = useState("");

  // Unit switched or value changed by the stepper: keep the field in sync.
  useEffect(() => {
    setTyped("");
    setTypedError("");
  }, [unit]);

  const step = (dir) => {
    setTyped("");
    setTypedError("");
    onChange(stepSize(value, unit, dir));
  };

  const onType = (e) => {
    const t = e.target.value;
    setTyped(t);
    if (!t.trim()) {
      setTypedError("");
      return;
    }
    const snapped = snapSize(parseSizeInput(t), unit);
    if (snapped === null) {
      onChange(null); // never keep a stale size behind an invalid entry
      setTypedError(`Use ${unit === "EU" ? "an" : "a"} ${unit} size like ${unit === "EU" ? "42, 42,5 or 42 2/3" : "8 or 8,5"}.`);
      return;
    }
    setTypedError("");
    onChange(snapped);
  };

  const display = value === null || value === undefined ? "" : formatSize(value);

  return (
    <div data-testid={testid}>
      <div className="mt-1.5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div
          className={`flex items-center justify-between rounded-2xl border bg-white p-1.5 sm:w-[260px] ${
            invalid ? "border-red-300" : "border-black/10"
          }`}
        >
          <button
            type="button"
            aria-label="Smaller size"
            data-testid={`${testid}-minus`}
            onClick={() => step(-1)}
            className="grid place-items-center h-11 w-11 rounded-xl bg-neutral-50 hover:bg-neutral-100 active:scale-95 transition-all"
          >
            <Minus className="h-4 w-4" />
          </button>
          <div className="flex items-baseline gap-1.5 leading-none" aria-live="polite">
            <span className="text-[13px] text-neutral-400">{unit}</span>
            <span
              data-testid={`${testid}-value`}
              className={`text-[32px] font-medium tracking-[-0.03em] tabular-nums ${display ? "" : "text-neutral-300"}`}
            >
              {display || "—"}
            </span>
          </div>
          <button
            type="button"
            aria-label="Larger size"
            data-testid={`${testid}-plus`}
            onClick={() => step(1)}
            className="grid place-items-center h-11 w-11 rounded-xl bg-neutral-50 hover:bg-neutral-100 active:scale-95 transition-all"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 sm:max-w-[170px]">
          <input
            data-testid={`${testid}-input`}
            inputMode="decimal"
            autoComplete="off"
            value={typed}
            onChange={onType}
            placeholder={unit === "EU" ? "or type, e.g. 42,5" : "or type, e.g. 8,5"}
            aria-label={`Type your ${unit} size`}
            className="w-full rounded-xl border border-black/10 bg-white px-3.5 h-11 text-[14px] text-[#0A0A0A] placeholder:text-neutral-300 outline-none focus:border-black/50 transition-colors"
          />
        </div>
      </div>
      {typedError && (
        <div data-testid={`${testid}-input-error`} className="mt-1.5 text-[12px] text-red-600">
          {typedError}
        </div>
      )}
    </div>
  );
}
